import {
  DynamicAttributeFieldFragment,
  DynamicAttributeFieldListFragment,
} from "gql/graphql";
import { FormListProps } from "antd/lib/form";
import Form from "@/shared/form/form";
import { DynamicAttributeField } from "../dynamic-attribute-field/dynamic-attribute-field";
import { transformMetaFieldsToAttributeValueInput } from "../../utils/transform-meta-to-attribute-value-input";
import { Fieldset } from "@/shared/form/fieldset";
import { getFieldNameByAttributeCode } from "./dynamic-attribute-field-list-utils";
import { ReactNode } from "react";
import { DynamicAttributeFieldFormItemProviderProps } from "../dynamic-attribute-field/dynamic-attribute-field-form-item-provider";
import { NamePath } from "antd/es/form/interface";

type DynamicAttributeFieldListProps = Omit<FormListProps, "children"> & {
  meta: DynamicAttributeFieldListFragment;
  children?: (
    meta: DynamicAttributeFieldListFragment,
    utils: {
      getNameByAttributeCode: (attributeCode: string) => NamePath;
    },
  ) => ReactNode;
};

export function DynamicAttributeFieldList({
  meta,
  children,
  ...rest
}: DynamicAttributeFieldListProps) {
  const form = Form.useFormInstance();

  const initialValue = transformMetaFieldsToAttributeValueInput(meta);

  return (
    <Form.List initialValue={initialValue} {...rest}>
      {(fields) => {
        if (children) {
          return children(meta, {
            getNameByAttributeCode: (attributeCode: string) =>
              getFieldNameByAttributeCode({
                attributeCode,
                fields,
                form,
                listName: rest.name,
              }),
          });
        } else {
          return (
            <div>
              {meta.attributes.map((meta) => (
                <DynamicAttributeField.FormItem
                  key={meta.attributeCode}
                  name={getFieldNameByAttributeCode({
                    attributeCode: meta.attributeCode,
                    fields,
                    form,
                    listName: rest.name,
                  })}
                  label={meta.name.value}
                >
                  <DynamicAttributeField meta={meta} />
                </DynamicAttributeField.FormItem>
              ))}
              {meta.groups.map((groupMeta) => (
                <Fieldset title={groupMeta.name.value} key={groupMeta.id}>
                  {groupMeta.attributes.map((attributeMeta) => (
                    <DynamicAttributeField.FormItem
                      key={attributeMeta.attributeCode}
                      name={getFieldNameByAttributeCode({
                        attributeCode: attributeMeta.attributeCode,
                        fields,
                        form,
                        listName: rest.name,
                      })}
                      label={attributeMeta.name.value}
                    >
                      <DynamicAttributeField meta={attributeMeta} />
                    </DynamicAttributeField.FormItem>
                  ))}
                </Fieldset>
              ))}
            </div>
          );
        }
      }}
    </Form.List>
  );
}

function DynamicAttributeFieldListItem({
  attributeMeta,
}: {
  attributeMeta: DynamicAttributeFieldFragment;
}) {
  return <DynamicAttributeField meta={attributeMeta} />;
}

function DynamicAttributeFieldListFormItem(
  props: DynamicAttributeFieldFormItemProviderProps,
) {
  return <DynamicAttributeField.FormItem {...props} />;
}

DynamicAttributeFieldList.Item = DynamicAttributeFieldListItem;
DynamicAttributeFieldList.FormItem = DynamicAttributeFieldListFormItem;
DynamicAttributeFieldList.ControlProps = DynamicAttributeField.ControlProps;
